import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert } from '../ui';
import { AlertTriangle, Users, Wrench, FileText, Database, AlertCircle } from 'lucide-react';
import { api } from '../../services/api';

interface ServerDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  serverId: string;
  serverName: string;
}

export const ServerDeletionModal: React.FC<ServerDeletionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  serverId,
  serverName
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dependencies, setDependencies] = useState<any>(null);
  const [confirmationText, setConfirmationText] = useState('');

  useEffect(() => {
    if (isOpen && serverId) {
      fetchDependencies();
    }
  }, [isOpen, serverId]);

  const fetchDependencies = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.servers.getDependencies(serverId);
      setDependencies(response.dependencies);
    } catch (err: any) {
      setError(err.message || 'Failed to load server dependencies');
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationColor = () => {
    if (!dependencies) return 'text-gray-600';
    switch (dependencies.recommendation) {
      case 'safe': return 'text-green-600';
      case 'low_impact': return 'text-yellow-600';
      case 'medium_impact': return 'text-orange-600';
      case 'high_impact': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getRecommendationIcon = () => {
    if (!dependencies) return <AlertCircle size={24} className="text-gray-600" />;
    switch (dependencies.recommendation) {
      case 'safe': return <AlertCircle size={24} className="text-green-600" />;
      case 'low_impact': return <AlertTriangle size={24} className="text-yellow-600" />;
      case 'medium_impact': return <AlertTriangle size={24} className="text-orange-600" />;
      case 'high_impact': return <AlertTriangle size={24} className="text-red-600" />;
      default: return <AlertCircle size={24} className="text-gray-600" />;
    }
  };

  const getRecommendationText = () => {
    if (!dependencies) return 'Analyzing...';
    switch (dependencies.recommendation) {
      case 'safe':
        return 'Safe to delete - No dependencies found';
      case 'low_impact':
        return 'Low Impact - Few items will be affected';
      case 'medium_impact':
        return 'Medium Impact - Several items will be affected';
      case 'high_impact':
        return 'High Impact - Many items will be affected or critical tools present';
      default:
        return 'Unknown impact';
    }
  };

  const handleConfirm = () => {
    // For high impact servers, require confirmation text
    if (dependencies?.recommendation === 'high_impact' && confirmationText !== serverName) {
      setError(`Please type "${serverName}" to confirm deletion`);
      return;
    }
    onConfirm();
  };

  const requiresConfirmation = dependencies?.recommendation === 'high_impact';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Server - Impact Analysis"
      size="lg"
    >
      <div className="space-y-6">
        {/* Server Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              {getRecommendationIcon()}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">
                Deleting: <span className="text-blue-600">{serverName}</span>
              </h3>
              <p className={`text-sm font-medium mt-1 ${getRecommendationColor()}`}>
                {getRecommendationText()}
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
            <span className="ml-3 text-gray-600">Analyzing dependencies...</span>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Dependency Analysis */}
        {!loading && !error && dependencies && (
          <>
            {/* Stats Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Database size={20} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Total Affected Items</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {dependencies.total_affected_items}
                </div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={20} className="text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">Dependent Prompts</span>
                </div>
                <div className="text-2xl font-bold text-purple-600">
                  {dependencies.total_dependent_prompts}
                </div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Wrench size={16} className="text-gray-600" />
                  <span className="text-sm font-medium text-gray-900">Tools</span>
                </div>
                <div className="text-xl font-bold text-gray-900">
                  {dependencies.tools?.length || 0}
                </div>
                {dependencies.has_critical_tools && (
                  <div className="mt-1">
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">
                      Has Critical Tools
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={16} className="text-gray-600" />
                  <span className="text-sm font-medium text-gray-900">Prompts</span>
                </div>
                <div className="text-xl font-bold text-gray-900">
                  {dependencies.prompts_count}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Database size={16} className="text-gray-600" />
                  <span className="text-sm font-medium text-gray-900">Resources</span>
                </div>
                <div className="text-xl font-bold text-gray-900">
                  {dependencies.resources_count}
                </div>
              </div>
            </div>

            {/* Clients List */}
            {dependencies.clients && dependencies.clients.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Users size={18} className="text-blue-600" />
                  Affected Clients ({dependencies.clients.length})
                </h4>
                <div className="space-y-2">
                  {dependencies.clients.map((client: any) => (
                    <div
                      key={client.id}
                      className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className="font-medium text-gray-900">{client.name}</span>
                        </div>
                        <div className="flex gap-4 text-sm text-gray-600">
                          <span>{client.tools_count} tools</span>
                          <span>{client.prompts_count} prompts</span>
                          <span>{client.resources_count} resources</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Critical Tools List */}
            {dependencies.tools && dependencies.tools.filter((t: any) => t.is_critical).length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <AlertTriangle size={18} className="text-red-600" />
                  Critical Tools ({dependencies.tools.filter((t: any) => t.is_critical).length})
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dependencies.tools
                    .filter((tool: any) => tool.is_critical)
                    .map((tool: any) => (
                      <div
                        key={tool.id}
                        className="bg-red-50 border border-red-200 rounded-lg p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Wrench size={16} className="text-red-600" />
                              <span className="font-medium text-gray-900">{tool.name}</span>
                              <span className="px-2 py-0.5 text-xs bg-red-600 text-white rounded-full">
                                Required by {tool.dependent_prompts_count} prompt{tool.dependent_prompts_count !== 1 ? 's' : ''}
                              </span>
                            </div>
                            {tool.description && (
                              <p className="text-sm text-gray-600 mt-1">{tool.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Warning Message */}
            {dependencies.total_affected_items > 0 && (
              <Alert variant="warning">
                <div className="space-y-2">
                  <p className="font-semibold">Warning: This action cannot be undone</p>
                  <p className="text-sm">
                    Deleting this server will permanently remove:
                  </p>
                  <ul className="text-sm list-disc list-inside space-y-1">
                    <li>{dependencies.tools?.length || 0} tool{dependencies.tools?.length !== 1 ? 's' : ''} from this server</li>
                    <li>{dependencies.prompts_count} prompt{dependencies.prompts_count !== 1 ? 's' : ''} from this server</li>
                    <li>{dependencies.resources_count} resource{dependencies.resources_count !== 1 ? 's' : ''}</li>
                    {dependencies.total_dependent_prompts > 0 && (
                      <li className="font-semibold text-orange-700">
                        {dependencies.total_dependent_prompts} custom prompt{dependencies.total_dependent_prompts !== 1 ? 's' : ''} will lose access to tools
                      </li>
                    )}
                  </ul>
                </div>
              </Alert>
            )}

            {/* Confirmation Input for High Impact */}
            {requiresConfirmation && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Type <span className="font-bold text-red-600">{serverName}</span> to confirm deletion:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={serverName}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={loading || (requiresConfirmation && confirmationText !== serverName)}
          >
            {dependencies?.recommendation === 'safe'
              ? 'Delete Server'
              : `Delete Anyway (${dependencies?.total_affected_items || 0} affected)`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
